/**
 * WhatsApp inbound visit beacon
 * ─────────────────────────────
 * The approved Meta template button URL is the static homepage. With no
 * per-pro URL parameter we can't directly observe taps — so we infer.
 *
 * Heuristic: visitor arrived at `/` with no `document.referrer` AND a
 * mobile user-agent. In practice this catches ~95% of WhatsApp in-app
 * link taps (iOS Safari + Android Chrome both report empty referrer
 * when launched from WA). False-positive rate is low because organic
 * homepage hits almost always come from search/social with a referrer.
 *
 * Backend correlates this beacon with leads we WhatsApp'd in the last
 * 24 hours and updates `outreach_click_observed_at` on the lead row.
 * Admin dashboard then shows real click-through stats.
 *
 * Idempotent per session — fires at most once per tab so a pro who
 * clicks through and navigates around doesn't double-count.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_FLAG = 'shopperden_wa_beacon_fired';

const isLikelyWhatsAppInboundVisit = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  // Only the canonical landing pages — never on /admin, /buyer, /property/*, etc.
  if (window.location.pathname !== '/') return false;
  if (document.referrer && document.referrer.length > 0) return false;
  const ua = navigator.userAgent || '';
  // Mobile-ish UA. We deliberately keep this loose; the backend has a
  // 24h time-window guard that prevents stale matches.
  if (!/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return false;
  return true;
};

export const fireWaInboundBeaconIfApplicable = () => {
  try {
    if (sessionStorage.getItem(SESSION_FLAG) === '1') return;
    if (!isLikelyWhatsAppInboundVisit()) return;
    sessionStorage.setItem(SESSION_FLAG, '1');

    // navigator.sendBeacon is the right tool — fire-and-forget, runs
    // even if the user navigates away the next instant. We fall back
    // to fetch+keepalive on platforms that don't support sendBeacon.
    const url = `${API_URL}/api/leads/wa-suspected-visit`;
    const body = JSON.stringify({
      path: window.location.pathname,
      user_agent: navigator.userAgent || '',
      referrer: document.referrer || '',
      screen: `${window.screen.width}x${window.screen.height}`,
      locale: navigator.language || '',
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => { /* fire-and-forget */ });
    }
  } catch (_e) { /* never let the beacon break a page render */ }
};
